import React, { useId, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { ApiData, KeyValueItem, LabelKeyValueItem, ParagraphUtilityApi } from "@/model";
import { ParagraphInputsProps } from "../model";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { isValidParagraphUrl } from "@/util/helper/paragraphHelper";
import { isValidJSON } from "@/util/stringUtil";
import { dictToKeyValueArray, keyValueArrayToDict, getLabelKeyFromValue } from "@/util/typeUtil";
import Button from "@/component/Button";
import { DropdownGroup, DropdownButton, DropdownItem, DropdownItems, CodeEditor, TextInput } from "@/component/input";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";


const MODE_OPTIONS: LabelKeyValueItem<"default" | "stream">[] = [
    { value: "default", labelKey: "i04059" },
    { value: "stream", labelKey: "i04060" },
];

const METHOD_OPTIONS: LabelKeyValueItem<"GET" | "POST" | "PUT" | "DELETE">[] = [
    { value: "GET", labelKey: "i04061" },
    { value: "POST", labelKey: "i04062" },
    { value: "PUT", labelKey: "i04063" },
    { value: "DELETE", labelKey: "i04064" },
];

const ParagraphUtilityAPICardInputs: React.FC<ParagraphInputsProps<ParagraphUtilityApi>> = (props) => {
    const { paragraph, onChange } = props;

    const intl = useIntl();
    const urlErrorId = useId();

    const [headers, setHeaders] = useState<KeyValueItem<string>[]>(dictToKeyValueArray(paragraph.data?.headers ?? {}));
    const [params, setParams] = useState<KeyValueItem<string>[]>(dictToKeyValueArray(paragraph.data?.params ?? {}));

    const onChangeField = <T extends keyof ApiData>(key: T, value: ApiData[T]) => {
        onChange({
            ...paragraph,
            data: {
                ...paragraph.data,
                method: paragraph.data?.method ?? "GET",
                [key]: value
            }
        });
    };

    const onChangeHeaders = (headers: KeyValueItem<string>[]) => {
        setHeaders(headers);
        onChangeField("headers", keyValueArrayToDict(headers));
    };

    const onChangeParams = (params: KeyValueItem<string>[]) => {
        setParams(params);
        onChangeField("params", keyValueArrayToDict(params));
    };

    const urlInvalid = !!paragraph.data?.url && !isValidParagraphUrl(paragraph.data?.url ?? "");

    return (<>
        <fieldset role="radiogroup"
                  className="contents"
        >
            <legend className="field-label">
                {intl.formatMessage({ id: "i04065" })}
            </legend>
            <div className="flex gap-4 items-center">
                {MODE_OPTIONS.map(option => (
                    <label key={option.value}
                           className="flex gap-1 items-center cursor-pointer"
                    >
                        <input type="radio"
                               name={`${paragraph.id}-api-mode`}
                               className="input-radio-chat42 size-4"
                               checked={option.value === (paragraph.data?.stream_mode ? "stream" : "default")}
                               onChange={() => onChangeField("stream_mode", option.value === "stream")}
                        />
                        <div className="label-input-grid-label input-label">
                            {intl.formatMessage({ id: option.labelKey })}
                        </div>
                    </label>
                ))}
            </div>
        </fieldset>

        <div className="field-label">
            {intl.formatMessage({ id: "i04066" })}
        </div>
        <DropdownGroup>{({ open, close }) => (<>
            <DropdownButton open={open} close={close}>
                {intl.formatMessage({ id: getLabelKeyFromValue(METHOD_OPTIONS, paragraph.data?.method ?? "GET") })}
            </DropdownButton>
            <DropdownItems>
                {METHOD_OPTIONS.map((option, index) => (
                    <DropdownItem key={option.value}
                                  isOdd={index % 2 === 1}
                                  onClick={() => onChangeField("method", option.value)}
                    >
                        {intl.formatMessage({ id: option.labelKey })}
                    </DropdownItem>
                ))}
            </DropdownItems>
        </>)}</DropdownGroup>

        <div className="label-input-grid-label field-label">
            {intl.formatMessage({ id: "i04067" })}
        </div>
        <div className="flex flex-col gap-1">
            <TextInput required
                       invalid={!isValidParagraphUrl(paragraph.data?.url ?? "")}
                       aria-errormessage={urlInvalid ? urlErrorId : undefined}
                       defaultValue={paragraph.data?.url ?? ""}
                       placeholder={intl.formatMessage({ id: "i04051" })}
                       onChange={e => onChangeField("url", e.target.value)}
            />
            {urlInvalid &&
                <span id={urlErrorId}
                      className="text-invalid"
                >
                    {intl.formatMessage({ id: "i04077" })}
                </span>
            }
        </div>

        <div data-testid="paragraph-api-headers"
             className="col-span-2 flex flex-col gap-1.5"
        >
            <div className="h-6 flex items-center gap-1">
                <div className="field-label">
                    {intl.formatMessage({ id: "i04052" })}
                </div>
                <Button variant="text-transparent"
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => onChangeHeaders([...headers, { key: "", value: "" }])}
                >
                    <AddIcon className="size-3" />
                </Button>
            </div>
            <div className={classNames("flex flex-col gap-2", headers.length === 0 && "hidden")}>
                {headers.map(({ key, value }, index) => (
                    <div key={index} className="flex gap-1 items-center">
                        <TextInput required
                                   value={key}
                                   placeholder={intl.formatMessage({ id: "i04053" })}
                                   onChange={e => onChangeHeaders(withArray(headers, index, { key: e.target.value, value }))}
                        />
                        <TextInput required
                                   value={value}
                                   placeholder={intl.formatMessage({ id: "i04054" })}
                                   onChange={e => onChangeHeaders(withArray(headers, index, { key, value: e.target.value }))}
                        />
                        <Button variant="text-transparent"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={() => onChangeHeaders(toSpliced(headers, index, 1))}
                        >
                            <DeleteIcon className="size-4 text-point-red" />
                        </Button>
                    </div>
                ))}
            </div>
        </div>

        <div data-testid="paragraph-api-params"
             className="col-span-2 flex flex-col gap-1.5"
        >
            <div className="h-6 flex items-center gap-1">
                <div className="field-label">
                    {intl.formatMessage({ id: "i04055" })}
                </div>
                <Button variant="text-transparent"
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => onChangeParams([...params, { key: "", value: "" }])}
                >
                    <AddIcon className="size-3" />
                </Button>
            </div>
            <div className={classNames("flex flex-col gap-2", params.length === 0 && "hidden")}>
                {params.map(({ key, value }, index) => (
                    <div key={index} className="flex gap-1 items-center">
                        <TextInput required
                                   value={key}
                                   placeholder={intl.formatMessage({ id: "i04056" })}
                                   onChange={e => onChangeParams(withArray(params, index, { key: e.target.value, value }))}
                        />
                        <TextInput required
                                   value={value}
                                   placeholder={intl.formatMessage({ id: "i04057" })}
                                   onChange={e => onChangeParams(withArray(params, index, { key, value: e.target.value }))}
                        />
                        <Button variant="text-transparent"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={() => onChangeParams(toSpliced(params, index, 1))}
                        >
                            <DeleteIcon className="size-4 text-point-red" />
                        </Button>
                    </div>
                ))}
            </div>
        </div>

        <div className="col-span-2 flex flex-col gap-1.5">
            <div className="h-6 flex items-center field-label">
                {intl.formatMessage({ id: "i04058" })}
            </div>
            <CodeEditor mode="json"
                        height="250px"
                        defaultValue={paragraph.data?.body ?? ""}
                        inValid={!!paragraph.data?.body && !isValidJSON(paragraph.data?.body)}
                        invalidMessage={intl.formatMessage({ id: "i04033" })}
                        onChange={value => onChangeField("body", value)}
            />
        </div>
    </>);
};

export default ParagraphUtilityAPICardInputs;
